/* eslint-disable jsx-a11y/label-has-associated-control */
import * as Yup from 'yup';
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useFormik, FormikProvider } from 'formik';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import saveIcon from '@iconify/icons-eva/save-outline';
import cameraIcon from '@iconify/icons-ic/baseline-camera-alt';

// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Autocomplete,
  Avatar,
  Badge
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { editItem } from '../../fetch';
import { usersActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import CountrySelect from '../CountrySelect';
import {
  selectAerosArray,
  selectGroupSelectedId,
  selectGroupsArray,
  selectSession
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

UserForm.propTypes = {
  userId: PropTypes.number,
  user: PropTypes.object
};

export default function UserForm({ userId, user }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const groups = useSelector(selectGroupsArray);
  const aeros = useSelector(selectAerosArray);

  const TABLE_STATUS_ALL = (() => {
    if (session.administrator) {
      return [
        { id: 0, name: t('fields_Admin') },
        { id: 1, name: t('fields_Manager') },
        { id: 2, name: t('fields_Instructor') },
        { id: 3, name: t('fields_Pilot+') },
        { id: 4, name: t('fields_Pilot') }
      ];
    }

    if (permission.status === 0 || permission.status === 1) {
      return [
        { id: 1, name: t('fields_Manager') },
        { id: 2, name: t('fields_Instructor') },
        { id: 3, name: t('fields_Pilot+') },
        { id: 4, name: t('fields_Pilot') }
      ];
    }
    return [
      { id: 3, name: t('fields_Pilot+') },
      { id: 4, name: t('fields_Pilot') }
    ];
  })();
  const TABLE_NIVEAU = [
    { id: 'student', name: t('fields_Student') },
    { id: 'studentSolo', name: t('fields_Student_Solo') },
    { id: 'licensedPilot', name: t('fields_Licensed_Pilot') },
    { id: 'instructor', name: t('fields_Instructor') }
  ];
  const initialUser = {
    name: '',
    prenom: '',
    email: '',
    password: '',
    phone: '',
    groupId: session.administrator ? 'null' : groupId,
    status: '',
    niveau: '',
    adresse: '',
    adresseSuite: '',
    codePostal: '',
    ville: '',
    country: 'FR',
    uniteAltitude: 'ft',
    uniteDistance: 'mille',
    uniteTemp: '°C',
    uniteVitesse: 'kt',
    attributes: { geofenceFlag: true },
    map: 'Carto',
    aero: ''
  };
  const [showPassword, setShowPassword] = useState(false);

  const RegisterSchema = Yup.object().shape({
    prenom: Yup.string()
      .min(2, t('yupValid_Short'))
      .max(50, t('yupValid_Long'))
      .required(t('yupValid_Required')),
    name: Yup.string()
      .min(2, t('yupValid_Short'))
      .max(50, t('yupValid_Long'))
      .required(t('yupValid_Required')),
    codePostal: Yup.number(),
    email: Yup.string().email(t('yupValid_Invalid')).required(t('yupValid_Required')),
    password: user === undefined && Yup.string().required(t('yupValid_Required'))
  });

  const formik = useFormik({
    initialValues: user || initialUser,
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      const { groupId, status } = values;
      const body = values;
      delete body.groupId;
      delete body.status;
      delete body.permission;

      const response = await editItem('users', userId, body);
      if (response.ok) {
        enqueueSnackbar(user !== undefined ? t('UL_Snackbar_Change') : t('UL_Snackbar_Add'), {
          variant: 'success'
        });
        const result = await response.json();
        if (user === undefined && groupId && status) {
          const responsePermission = await editItem('permissions', undefined, {
            userId: result.id,
            groupId,
            status
          });
          if (responsePermission.ok) {
            dispatch(
              usersActions.update([
                {
                  ...result,
                  permission: [
                    {
                      userid: result.id,
                      groupid: groupId,
                      status
                    }
                  ]
                }
              ])
            );
            navigate('/user', { replace: true });
          }
        } else {
          dispatch(usersActions.update([result]));
          navigate('/user', { replace: true });
        }
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
        setSubmitting(false);
      }
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  const handleChangeImageFile = (event) => {
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);

    reader.onload = (e) => {
      setFieldValue('userImage', e.target.result);
    };
  };

  return (
    <FormikProvider value={formik}>
      <Stack spacing={3}>
        {user !== undefined && (
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 4 }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              badgeContent={
                <>
                  <input
                    accept="image/*"
                    id="icon-button-file"
                    hidden
                    type="file"
                    onChange={handleChangeImageFile}
                  />
                  <label htmlFor="icon-button-file">
                    <IconButton
                      sx={{
                        color: 'black',
                        backgroundColor: '#D3D3D3'
                      }}
                      aria-label="upload picture"
                      component="span"
                    >
                      <Icon icon={cameraIcon} width={30} height={30} />
                    </IconButton>
                  </label>
                </>
              }
            >
              <Avatar
                alt="UserImage"
                sx={{ width: 200, height: 200 }}
                src={getFieldProps('userImage').value}
              />
            </Badge>
          </Stack>
        )}
        {session.administrator && user === undefined && (
          <Autocomplete
            fullWidth
            {...getFieldProps('groupId')}
            options={groups}
            getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
            value={groups.find((group) => group.id === getFieldProps('groupId').value) || null}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('fields_Club')}
                error={Boolean(touched.club && errors.club)}
                helperText={touched.club && errors.club}
              />
            )}
            onChange={(event, value) => setFieldValue('groupId', value !== null ? value.id : 0)}
          />
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('fields_FirstName')}
            {...getFieldProps('prenom')}
            value={getFieldProps('prenom').value || ''}
            error={Boolean(touched.prenom && errors.prenom)}
            helperText={touched.prenom && errors.prenom}
          />

          <TextField
            fullWidth
            label={t('fields_LastName')}
            {...getFieldProps('name')}
            value={getFieldProps('name').value || ''}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />
        </Stack>

        <TextField
          fullWidth
          autoComplete="address"
          label={t('fields_Address')}
          {...getFieldProps('adresse')}
          value={getFieldProps('adresse').value || ''}
          error={Boolean(touched.adresse && errors.adresse)}
          helperText={touched.adresse && errors.adresse}
        />
        <TextField
          fullWidth
          autoComplete="address"
          label={t('fields_ExtraAddress')}
          {...getFieldProps('adresseSuite')}
          value={getFieldProps('adresseSuite').value || ''}
          error={Boolean(touched.adresseSuite && errors.adresseSuite)}
          helperText={touched.adresseSuite && errors.adresseSuite}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('fields_City')}
            {...getFieldProps('ville')}
            value={getFieldProps('ville').value || ''}
            error={Boolean(touched.ville && errors.ville)}
            helperText={touched.ville && errors.ville}
          />

          <TextField
            fullWidth
            label={t('fields_Zip')}
            type="number"
            {...getFieldProps('codePostal')}
            value={getFieldProps('codePostal').value || ''}
            error={Boolean(touched.codePostal && errors.codePostal)}
            helperText={touched.codePostal && errors.codePostal}
          />
        </Stack>

        <CountrySelect formik={formik} />

        <Autocomplete
          fullWidth
          {...getFieldProps('aero')}
          options={aeros}
          getOptionLabel={(option) =>
            option.oaci === undefined ? '' : `${option.oaci} - ${option.nom}`
          }
          value={aeros.find((aero) => aero.oaci === getFieldProps('aero').value) || null}
          renderInput={(params) => <TextField {...params} label={t('fields_Aerodrome')} />}
          onChange={(event, value) => setFieldValue('aero', value !== null ? value.oaci : 0)}
        />

        <TextField
          fullWidth
          autoComplete="telephone"
          type="number"
          label={t('fields_Phone')}
          {...getFieldProps('phone')}
          value={getFieldProps('phone').value || ''}
          error={Boolean(touched.phone && errors.phone)}
          helperText={touched.phone && errors.phone}
        />
        {user === undefined && (
          <Autocomplete
            fullWidth
            {...getFieldProps('status')}
            value={
              TABLE_STATUS_ALL.find((item) => item.id === getFieldProps('status').value) || null
            }
            options={TABLE_STATUS_ALL}
            getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('fields_Status')}
                error={Boolean(touched.status && errors.status)}
                helperText={touched.status && errors.status}
              />
            )}
            onChange={(event, value) => setFieldValue('status', value !== null ? value.id : '')}
          />
        )}
        <Autocomplete
          fullWidth
          {...getFieldProps('niveau')}
          value={TABLE_NIVEAU.find((item) => item.id === getFieldProps('niveau').value) || null}
          options={TABLE_NIVEAU}
          getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
          renderInput={(params) => <TextField {...params} label={t('fields_Level')} />}
          onChange={(event, value) => setFieldValue('niveau', value !== null ? value.id : '')}
        />

        <TextField
          fullWidth
          autoComplete="email"
          type="email"
          label={t('fields_Email')}
          {...getFieldProps('email')}
          value={getFieldProps('email').value || ''}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
        />

        {user !== undefined && user !== null && session.administrator && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={t('fields_ExpirationDate')}
              inputFormat="DD/MM/YYYY"
              value={getFieldProps('expirationTime').value}
              onChange={(e) => setFieldValue('expirationTime', e)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={Boolean(touched.expirationTime && errors.expirationTime)}
                  helperText={touched.expirationTime && errors.expirationTime}
                />
              )}
            />
          </LocalizationProvider>
        )}

        {(session.administrator || (!session.administrator && user === undefined)) && (
          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label={t('fields_Password')}
            {...getFieldProps('password')}
            value={getFieldProps('password').value || ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        )}

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          startIcon={<Icon icon={saveIcon} />}
          onClick={handleSubmit}
        >
          {t('btn_Save')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
